import React, { createContext, useContext, useRef, useEffect } from 'react';
import sound1 from './images/sound1.mp3';
import sound2 from './images/sound2.mp3';
import wishSound from './images/wish.mp3';

const AudioContext = createContext(null);

export function AudioProvider({ children }) {
  const sound1Ref = useRef(null);
  const sound2Ref = useRef(null);
  const wishSoundRef = useRef(null);
  const currentSoundRef = useRef(null); // 'sound1' | 'sound2' | 'wish' | null

  useEffect(() => {
    // Initialize audio elements
    sound1Ref.current = new Audio(sound1);
    sound1Ref.current.loop = true;
    sound1Ref.current.volume = 1;
    
    sound2Ref.current = new Audio(sound2);
    sound2Ref.current.loop = true;
    sound2Ref.current.volume = 1;

    wishSoundRef.current = new Audio(wishSound);
    wishSoundRef.current.loop = true;
    wishSoundRef.current.volume = 1;

    return () => {
      // Cleanup
      if (sound1Ref.current) {
        sound1Ref.current.pause();
        sound1Ref.current = null;
      }
      if (sound2Ref.current) {
        sound2Ref.current.pause();
        sound2Ref.current = null;
      }
      if (wishSoundRef.current) {
        wishSoundRef.current.pause();
        wishSoundRef.current = null;
      }
    };
  }, []);

  const playSound1 = async () => {
    if (currentSoundRef.current === 'sound1' && sound1Ref.current && !sound1Ref.current.paused) {
      return; // Already playing
    }
    
    // Stop sound2 if playing
    if (currentSoundRef.current === 'sound2' && sound2Ref.current) {
      sound2Ref.current.pause();
      sound2Ref.current.currentTime = 0;
    }
    
    // Play sound1
    if (sound1Ref.current) {
      try {
        // If already paused, resume from current position
        if (sound1Ref.current.paused) {
          await sound1Ref.current.play();
        }
        currentSoundRef.current = 'sound1';
      } catch (e) {
        // Autoplay was prevented, will retry on user interaction
        console.log('Audio play failed (will retry on user interaction):', e);
        currentSoundRef.current = null; // Reset so it can retry
      }
    }
  };

  const playSound2 = async () => {
    if (currentSoundRef.current === 'sound2') return; // Already playing
    
    // Stop sound1 if playing
    if (currentSoundRef.current === 'sound1' && sound1Ref.current) {
      sound1Ref.current.pause();
      sound1Ref.current.currentTime = 0;
    }
    
    // Stop wish sound if playing
    if (currentSoundRef.current === 'wish' && wishSoundRef.current) {
      wishSoundRef.current.pause();
      wishSoundRef.current.currentTime = 0;
    }
    
    // Play sound2
    if (sound2Ref.current) {
      try {
        await sound2Ref.current.play();
        currentSoundRef.current = 'sound2';
      } catch (e) {
        console.log('Audio play failed:', e);
      }
    }
  };

  const playWishSound = async () => {
    if (currentSoundRef.current === 'wish' && wishSoundRef.current && !wishSoundRef.current.paused) {
      return; // Already playing
    }
    
    // Stop sound1 if playing
    if (currentSoundRef.current === 'sound1' && sound1Ref.current) {
      sound1Ref.current.pause();
      sound1Ref.current.currentTime = 0;
    }
    
    // Stop sound2 if playing
    if (currentSoundRef.current === 'sound2' && sound2Ref.current) {
      sound2Ref.current.pause();
      sound2Ref.current.currentTime = 0;
    }
    
    // Play wish sound
    if (wishSoundRef.current) {
      try {
        // Always try to play, even if not paused (to ensure it plays)
        wishSoundRef.current.currentTime = 0; // Reset to start
        await wishSoundRef.current.play();
        currentSoundRef.current = 'wish';
      } catch (e) {
        console.log('Audio play failed (will retry on user interaction):', e);
        currentSoundRef.current = null; // Reset so it can retry
      }
    }
  };

  const stopAll = () => {
    if (sound1Ref.current) {
      sound1Ref.current.pause();
      sound1Ref.current.currentTime = 0;
    }
    if (sound2Ref.current) {
      sound2Ref.current.pause();
      sound2Ref.current.currentTime = 0;
    }
    if (wishSoundRef.current) {
      wishSoundRef.current.pause();
      wishSoundRef.current.currentTime = 0;
    }
    currentSoundRef.current = null;
  };

  return (
    <AudioContext.Provider value={{ playSound1, playSound2, playWishSound, stopAll }}>
      {children}
    </AudioContext.Provider>
  );
}

export function useAudio() {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider');
  }
  return context;
}

